import React, { Suspense } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { BrowserRouter } from "react-router-dom";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import App from "./App";
import { Context, stores } from "./context";
import "./index.css";
import Spinner from "./components/ui/Spinner";
import "./i18n/i18n.instance";

// En: Initialize Sentry for error tracking and performance monitoring
Sentry.init({
  dsn: "https://2ace83bc5254f3f7f7b343f5908432bd@o4511671430348800.ingest.de.sentry.io/4511671616536656",
  environment: import.meta.env.MODE,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // En: Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// --- Leaflet Icon Fix ---
// This is the correct, global place for this configuration.
// It ensures that Leaflet's default icons work everywhere in the app.
L.Icon.Default.imagePath = "/images/leaflet-images/";

// En: Create a root container for the React application and render the App component within it.
const container = document.getElementById("root"); // Або "app" (залежно від твоєї розмітки в index.html)
const root = createRoot(container!);

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={
        <div className="flex flex-col justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Ups! Something went wrong.
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            We are already aware of the issue and are working to fix it.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-emerald-500 text-white rounded hover:bg-emerald-600 dynamic-shadow"
          >
            Update the page
          </button>
        </div>
      }
    >
      <Context.Provider value={stores}>
        <GoogleReCaptchaProvider
          reCaptchaKey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || ""}
          scriptProps={{
            async: true,
            defer: true,
            appendTo: "head",
          }}
        >
          <BrowserRouter>
            <Suspense
              fallback={
                <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
                  <Spinner />
                </div>
              }
            >
              <App />
            </Suspense>
          </BrowserRouter>
        </GoogleReCaptchaProvider>
      </Context.Provider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
